/**
 * Minimal NDEF record helpers for CheckPoint NFC tags.
 *
 * A CheckPoint tag holds one NFC Forum URI record: a tag link carrying the short lookup
 * code (see tag-link.ts). Tags written before tag links hold a Text record with the bare
 * code; those still read. Records use the plugin's JSON shape:
 * byte arrays for type/id/payload.
 */

export interface NdefRecordData {
  tnf: number;
  type: number[];
  id: number[];
  payload: number[];
}

/** TNF 0x01: NFC Forum well-known type */
const TNF_WELL_KNOWN = 0x01;
const TYPE_TEXT = 0x54; // 'T'
const TYPE_URI = 0x55; // 'U'

/** URI identifier codes (NFC Forum URI RTD, table 3), index = prefix byte */
const URI_PREFIXES = [
  '', 'http://www.', 'https://www.', 'http://', 'https://', 'tel:', 'mailto:',
  'ftp://anonymous:anonymous@', 'ftp://ftp.', 'ftps://', 'sftp://', 'smb://', 'nfs://',
  'ftp://', 'dav://', 'news:', 'telnet://', 'imap:', 'rtsp://', 'urn:', 'pop:', 'sip:',
  'sips:', 'tftp:', 'btspp://', 'btl2cap://', 'btgoep://', 'tcpobex://', 'irdaobex://',
  'file://', 'urn:epc:id:', 'urn:epc:tag:', 'urn:epc:pat:', 'urn:epc:raw:', 'urn:epc:',
  'urn:nfc:',
];

/** Builds a UTF-8 Text record ("en"). */
export function textRecord(text: string, lang = 'en'): NdefRecordData {
  const langBytes = Array.from(new TextEncoder().encode(lang));
  const textBytes = Array.from(new TextEncoder().encode(text));
  // Status byte: bit 7 = 0 (UTF-8), bits 5..0 = language code length
  return {
    tnf: TNF_WELL_KNOWN,
    type: [TYPE_TEXT],
    id: [],
    payload: [langBytes.length & 0x3f, ...langBytes, ...textBytes],
  };
}

/** Builds a URI record, abbreviating the longest matching prefix ("https://" → 0x04). */
export function uriRecord(uri: string): NdefRecordData {
  let prefixCode = 0;
  URI_PREFIXES.forEach((prefix, i) => {
    if (prefix && uri.startsWith(prefix) && prefix.length > URI_PREFIXES[prefixCode].length) prefixCode = i;
  });
  const rest = uri.slice(URI_PREFIXES[prefixCode].length);
  return {
    tnf: TNF_WELL_KNOWN,
    type: [TYPE_URI],
    id: [],
    payload: [prefixCode, ...new TextEncoder().encode(rest)],
  };
}

/**
 * Size in bytes of an NDEF message of these records as written to a tag
 * (short-record headers; payloads here are always < 256 bytes).
 */
export function ndefMessageSize(records: NdefRecordData[]): number {
  return records.reduce((sum, r) => {
    const shortRecord = r.payload.length < 256;
    const header = 1 + 1 + (shortRecord ? 1 : 4) + (r.id.length ? 1 : 0);
    return sum + header + r.type.length + r.id.length + r.payload.length;
  }, 0);
}

/** Decodes a Text or URI record to a string; null for any other record type. */
export function decodeRecord(record: NdefRecordData): string | null {
  if (record.tnf !== TNF_WELL_KNOWN || record.type.length !== 1 || !record.payload.length) {
    return null;
  }
  const decoder = new TextDecoder('utf-8', { fatal: false });
  if (record.type[0] === TYPE_TEXT) {
    const status = record.payload[0];
    const langLength = status & 0x3f;
    const bytes = new Uint8Array(record.payload.slice(1 + langLength));
    // Bit 7 set = UTF-16 text
    return (status & 0x80 ? new TextDecoder('utf-16') : decoder).decode(bytes);
  }
  if (record.type[0] === TYPE_URI) {
    const prefix = URI_PREFIXES[record.payload[0]] ?? '';
    return prefix + decoder.decode(new Uint8Array(record.payload.slice(1)));
  }
  return null;
}

/** True when a tag's message has no meaningful content (factory-blank or erased). */
export function isBlankMessage(records: NdefRecordData[] | null | undefined): boolean {
  if (!records?.length) return true;
  // An erased tag reads back as a single empty record (TNF 0x00)
  return records.every((r) => r.tnf === 0x00 && !r.payload.length);
}

/** Tag UID bytes as uppercase hex, e.g. "04A1B2C3D4E580". */
export function uidToHex(id: number[] | null | undefined): string | null {
  if (!id?.length) return null;
  return id.map((b) => (b & 0xff).toString(16).padStart(2, '0')).join('').toUpperCase();
}
